/**
 * ButtonBlock - Container for one or more buttons with layout options
 * Simplifies button group layouts
 */

import React from 'react'
import { Box } from '@mui/material'
import Button from '../elements/Button'

interface ButtonData {
	text: string
	href?: string
	onClick?: () => void
	style?: 'primary' | 'secondary' | 'outlined'
	openInNewTab?: boolean
}

interface ButtonBlockProps {
	buttons: ButtonData[]
	layout?: 'horizontal' | 'vertical'
	align?: 'left' | 'center' | 'right'
	spacing?: 'tight' | 'normal' | 'loose'
	size?: 'small' | 'medium' | 'large'
}

const ButtonBlock: React.FC<ButtonBlockProps> = ({
	buttons,
	layout = 'horizontal',
	align = 'left',
	spacing = 'normal',
	size = 'medium'
}) => {
	const getSpacing = () => {
		switch (spacing) {
			case 'tight':
				return 1
			case 'normal':
				return 2
			case 'loose':
				return 3
			default:
				return 2
		}
	}

	const getJustifyContent = () => {
		switch (align) {
			case 'left':
				return 'flex-start'
			case 'center':
				return 'center'
			case 'right':
				return 'flex-end'
			default:
				return 'flex-start'
		}
	}

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: layout === 'vertical' ? 'column' : { xs: 'column', sm: 'row' },
				justifyContent: getJustifyContent(),
				alignItems: layout === 'vertical' ? align : 'center',
				gap: getSpacing()
			}}
		>
			{buttons.map((button, index) => (
				<Button
					key={index}
					href={button.href}
					onClick={button.onClick}
					style={button.style}
					size={size}
					openInNewTab={button.openInNewTab}
				>
					{button.text}
				</Button>
			))}
		</Box>
	)
}

export default ButtonBlock
